/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { Else, If, Then, When } from "react-if";
import {
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { formatMoney, isNotNil } from "~/tools";
import { Editing } from "../helpers";
import { BudgetNumberInput } from "./BudgetNumberInput";

type Props = {
  monthlyBudget: number;
  childrenBudget?: number;
  editing: boolean;
  hasChanged: boolean;
  currency: string;
  warningCaption?: string;
  onChange: React.ComponentProps<typeof BudgetNumberInput>["onChange"];
  onSetType: (value: Editing) => void;
};

export function BudgetCell({
  monthlyBudget,
  childrenBudget,
  editing,
  hasChanged,
  onChange,
  warningCaption = "",
  currency,
  onSetType,
}: Props) {
  const showBudgetWarning =
    isNotNil(childrenBudget) && monthlyBudget < childrenBudget;

  const formattedWarningCaption = useMemo(
    () => (
      <span>
        {warningCaption} <br /> TOTAL{" "}
        {formatMoney(childrenBudget, currency, false)}{" "}
      </span>
    ),
    [childrenBudget, currency, warningCaption]
  );

  return (
    <If condition={editing}>
      <Then>
        <FlexContainer data-testid="budget-cell-write">
          <BudgetNumberInput
            value={monthlyBudget}
            hasChanged={hasChanged}
            hasWarning={showBudgetWarning}
            type={Editing.MONTHLY}
            onChange={onChange}
            onSetType={onSetType}
          />
          <When condition={showBudgetWarning}>
            <ExtendedTooltip caption={formattedWarningCaption}>
              <SVGIcon
                name="statusWarning"
                size="sm"
                css={{ marginLeft: theme.spacing.spacing01 }}
              />
            </ExtendedTooltip>
          </When>
        </FlexContainer>
      </Then>
      <Else>
        <FlexContainer justifyContent="center" alignItems="baseline">
          <Text tag="span" align="center" data-testid="budget-cell-read">
            {formatMoney(monthlyBudget, currency, false)}
          </Text>
          <When condition={showBudgetWarning}>
            <ExtendedTooltip caption={formattedWarningCaption}>
              <SVGIcon
                name="statusWarning"
                size="sm"
                css={{ marginLeft: theme.spacing.spacing02 }}
                data-testid="budget-warning"
              />
            </ExtendedTooltip>
          </When>
        </FlexContainer>
      </Else>
    </If>
  );
}
